/**
 * Resolve display names for trip-share roster members — mirrors iOS TripSharingService.prefetchDisplayNames.
 * Prefer connection labels, then GET /users/{sub}, then short sub label.
 */

import {
  getUserById,
  labelFromCommunityProfile,
} from './userProfileService';
import { shortSubLabel } from '../utils/tripShareInviteMatch';

export type NameSeed = {
  connectedUserId: string;
  displayName?: string | null;
  username?: string | null;
};

export function labelFromConnectionSeed(seed: NameSeed | undefined, sub: string): string | null {
  if (!seed) return null;
  const name = seed.displayName?.trim();
  if (name) return name;
  const username = seed.username?.trim();
  if (username) return username;
  return null;
}

/**
 * Build a sub → display name map.
 * `seeds` come from listConnections; missing names are fetched via getUserById.
 */
export async function resolveDisplayNames(
  subs: string[],
  seeds: NameSeed[] = []
): Promise<Record<string, string>> {
  const seedById = new Map(
    seeds
      .filter((s) => s.connectedUserId)
      .map((s) => [s.connectedUserId, s] as const)
  );
  const unique = Array.from(
    new Set(subs.map((s) => String(s ?? '').trim()).filter((s) => s.length > 0))
  );
  const out: Record<string, string> = {};

  await Promise.all(
    unique.map(async (sub) => {
      const fromSeed = labelFromConnectionSeed(seedById.get(sub), sub);
      if (fromSeed) {
        out[sub] = fromSeed;
        return;
      }
      try {
        const profile = await getUserById(sub);
        const label = labelFromCommunityProfile(profile);
        out[sub] = label || shortSubLabel(sub);
      } catch {
        out[sub] = shortSubLabel(sub);
      }
    })
  );

  return out;
}

export function displayNameForSub(
  names: Record<string, string>,
  sub: string
): string {
  const key = String(sub ?? '').trim();
  return names[key] || shortSubLabel(key);
}
